import type { stacks } from "@dropbox/api-v2-client";
import { IconButton } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text, Title } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ChevronUpLine,
  DeleteLine,
  EditLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Cancel_DashMoreActions } from "@mirage/analytics/events/types/cancel_dash_more_actions";
import { PAP_Click_DashMoreActions } from "@mirage/analytics/events/types/click_dash_more_actions";
import { PAP_Delete_StackSection } from "@mirage/analytics/events/types/delete_stack_section";
import { PAP_Rename_StackSection } from "@mirage/analytics/events/types/rename_stack_section";
import { tagged } from "@mirage/service-logging";
import { upsertStack } from "@mirage/service-stacks";
import { stackDerivePAPProps } from "@mirage/service-stacks/service/utils";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { nonNil } from "@mirage/shared/util/tiny-utils";
import {
  activeStackActionAtom,
  activeStackAtom,
  activeStackHasWritePermissionsAtom,
  activeStackMutationRequestIdAtom,
  activeStackSectionIdAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/atoms";
import { useSectionsNavigation } from "@mirage/stacks/hooks/sectionHooks";
import i18n, {
  meticulousRedactedStringClass as redacted,
} from "@mirage/translations";
import classnames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { SectionsActionType } from "../../types/FullScreenTypes";
import { asStackUpsertId } from "../../utils";
import { AddItemButtonAugRev } from "./AddItemButton";
import { FancyInlineEdit } from "./FancyInlineEdit";
import styles from "./Header.module.css";
import { HeaderPopover } from "./HeaderPopover";
import { useContentSuggestions } from "./hooks";

const logger = tagged("Sections/Header");

export const Header = ({
  isCollapsed = false,
  linkCount,
  section,
  setIsCollapsed,
}: {
  isCollapsed?: boolean;
  linkCount: number;
  section: stacks.Section;
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { getSectionRef, setNewestSectionId, newestSectionId } =
    useSectionsNavigation();
  const mutationId = useAtomValue(activeStackMutationRequestIdAtom);
  const [, setSectionTitle] = useState(section.title ?? "");
  const [currentAction, setCurrentAction] = useAtom(activeStackActionAtom);
  const stack = useAtomValue(activeStackAtom);
  const [currentSectionId, setCurrentSectionId] = useAtom(
    activeStackSectionIdAtom,
  );
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);
  const [{ allSections: sections }, setSections] = useAtom(
    activeStackSectionsAtom,
  );
  const sectionHeaderRef = useRef<HTMLDivElement>(null);
  const isMobileSize = useIsMobileSize();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const sectionId = nonNil(section.id, "section.id");
  const sectionRef = getSectionRef(sectionId);
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const shouldShowHeaderActionButtons = isMobileSize || isTouchDevice;

  const closePopover = () => {
    setTimeout(() => {
      setIsPopoverOpen(false);
    }, 200);
  };

  // TODO extract into hook
  const onDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!sections) return;

      const newSections = sections.filter(
        (section) => section.id !== sectionId,
      );
      if (stack && stack.namespace_id) {
        reportPapEvent(
          PAP_Delete_StackSection({
            ...stackDerivePAPProps(stack),
            featureLine: "stacks",
          }),
        );
      }
      const update: stacks.StackDataUpdate = {
        field: {
          ".tag": "section_data_update",
          sections: newSections,
        },
      };
      await upsertStack(asStackUpsertId(stack?.namespace_id, mutationId), [
        update,
      ]);
      setSections(newSections);
    },
    [reportPapEvent, sections, setSections, stack, mutationId],
  );

  // TODO extract into hook
  const onRenameSection = useCallback(
    async (newName: string, sectionId: string) => {
      if (!sections) return;

      const newSections = sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            title: newName,
          };
        }
        return section;
      });
      if (stack && stack.namespace_id) {
        reportPapEvent(
          PAP_Rename_StackSection({
            ...stackDerivePAPProps(stack),
            featureLine: "stacks",
          }),
        );
      }
      const update: stacks.StackDataUpdate = {
        field: {
          ".tag": "section_data_update",
          sections: newSections,
        },
      };
      setSections(newSections);
      try {
        await upsertStack(asStackUpsertId(stack?.namespace_id, mutationId), [
          update,
        ]);
      } catch (error) {
        logger.error("Failed to rename section:", error);
        setSections(sections); // Revert to previous state on error
      }
    },
    [reportPapEvent, sections, setSections, stack, mutationId],
  );

  const handleRenameSubmit = async (title: string) => {
    setIsEditing(false);
    setCurrentAction(null);
    onRenameSection(title, sectionId);
  };

  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
    setCurrentAction(SectionsActionType.EDIT_SECTION);
    setCurrentSectionId(sectionId);
  }, [setCurrentAction, setCurrentSectionId, sectionId]);

  useEffect(() => {
    if (
      currentAction !== SectionsActionType.EDIT_SECTION ||
      currentSectionId !== sectionId
    ) {
      setIsEditing(false);
    }
  }, [currentAction, currentSectionId, sectionId]);

  useEffect(() => {
    sectionRef?.current?.addEventListener("edit", handleStartEditing);

    return () => {
      sectionRef?.current?.removeEventListener("edit", handleStartEditing);
    };
  }, [handleStartEditing, sectionRef]);

  useEffect(() => {
    if (newestSectionId === sectionId) {
      setSectionTitle("");
      handleStartEditing();
      sectionRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setNewestSectionId(null); // Reset after scrolling
    } else {
      setSectionTitle(section.title ?? "");
    }
  }, [
    handleStartEditing,
    newestSectionId,
    section.title,
    sectionId,
    sectionRef,
    setNewestSectionId,
  ]);

  return (
    <div
      className={styles.headerContainer}
      ref={sectionRef}
      onMouseEnter={() => setIsPopoverOpen(true)}
      onMouseLeave={closePopover}
    >
      {hasWritePermissions && (
        <HeaderPopover
          isOpen={!isEditing && isPopoverOpen}
          anchorRef={sectionHeaderRef}
          onAdd={() => {}}
          onEdit={() => handleStartEditing()}
          onDelete={() => onDeleteSection(sectionId)}
          section={section}
        />
      )}
      <div className={styles.titleContainer} ref={sectionHeaderRef}>
        <Title
          className={classnames(styles.title, redacted)}
          weightVariant="emphasized"
        >
          {isEditing && section.id === currentSectionId ? (
            <FancyInlineEdit
              data-testid="SectionRenameInput"
              title={section.title ?? ""}
              onSubmit={handleRenameSubmit}
              onCancel={() => {
                setIsEditing(false);
                setCurrentAction(null);
                setCurrentSectionId(null);
              }}
            />
          ) : (
            <DigTooltip title={section.title ?? ""} placement="right-start">
              <span>
                <Truncate>{section.title}</Truncate>
              </span>
            </DigTooltip>
          )}
        </Title>
        <ExpandCollapseButton
          isCollapsed={isCollapsed}
          linkCount={linkCount}
          setIsCollapsed={setIsCollapsed}
        />
        {isCollapsed && !isEditing && (
          <>
            <Text color="faint" size="large">
              {i18n.t("num_items", {
                count: linkCount,
              })}
            </Text>
          </>
        )}
      </div>
      <div className={styles.actionsContainer} data-testid="actionsContainer">
        {!isEditing && hasWritePermissions && shouldShowHeaderActionButtons && (
          <HeaderMenu
            isHoveringSection
            onDeleteSection={() => onDeleteSection(sectionId)}
            onEditSection={() => {
              setIsEditing(true);
              setCurrentAction(SectionsActionType.EDIT_SECTION);
              setCurrentSectionId(sectionId);
            }}
            section={section}
            stack={stack}
            showAddToSectionButton={true}
          />
        )}
      </div>
    </div>
  );
};

const ExpandCollapseButton = ({
  linkCount,
  isCollapsed,
  setIsCollapsed,
}: {
  linkCount: number;
  isCollapsed: boolean;
  setIsCollapsed?: (x: boolean) => void;
}) => {
  if (linkCount === 0) {
    return null;
  }

  return (
    <IconButton
      aria-label={isCollapsed ? i18n.t("expand") : i18n.t("collapse")}
      onClick={() => setIsCollapsed?.(!isCollapsed)}
      variant="transparent"
    >
      <UIIcon src={isCollapsed ? ChevronDownLine : ChevronUpLine} />
    </IconButton>
  );
};

export const HeaderMenu = ({
  isHoveringSection,
  onDeleteSection,
  onEditSection,
  section,
  setIsHoveringOnMenu,
  showAddToSectionButton,
  stack,
}: {
  isHoveringSection?: boolean;
  onDeleteSection: () => void;
  onEditSection: () => void;
  section: stacks.Section;
  setIsHoveringOnMenu?: (value: boolean) => void;
  showAddToSectionButton?: boolean;
  stack: stacks.Stack | null;
}) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);
  const { contentSuggestions, isFetchingContentSuggestions } =
    useContentSuggestions();

  const handleClickDashMore = useCallback(
    (isOpen: boolean) => {
      if (!stack) return;
      const papFn = isOpen
        ? PAP_Click_DashMoreActions
        : PAP_Cancel_DashMoreActions;
      reportPapEvent(
        papFn({
          ...stackDerivePAPProps(stack),
          featureLine: "stacks",
        }),
      );
    },
    [stack, reportPapEvent],
  );

  return (
    <>
      {showAddToSectionButton && hasWritePermissions && (
        <AddItemButtonAugRev
          contentSuggestions={contentSuggestions}
          loadingSuggestions={isFetchingContentSuggestions}
          section={section}
          variant="icon"
        />
      )}
      <Menu.Wrapper
        className={classnames(
          styles.sectionHeaderMenu,
          isHoveringSection && styles.sectionHeaderVisible,
        )}
        onToggle={({ isOpen }) => {
          handleClickDashMore(isOpen);
        }}
        onMouseEnter={() => setIsHoveringOnMenu?.(true)}
        onMouseLeave={() => setIsHoveringOnMenu?.(false)}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <DigTooltip title={i18n.t("more")}>
              <div>
                <IconButton variant="transparent" {...getTriggerProps()}>
                  <UIIcon
                    src={MoreHorizontalLine}
                    style={{ color: "var(--dig-color__text__subtle)" }}
                  />
                </IconButton>
              </div>
            </DigTooltip>
            <Menu.Content
              {...getContentProps()}
              id="stack_section-header-menu"
              placement="bottom-end"
            >
              <Menu.Segment>
                <Menu.ActionItem
                  withLeftAccessory={<UIIcon src={EditLine} />}
                  onClick={onEditSection}
                  data-testid="renameSection"
                >
                  {i18n.t("rename_section")}
                </Menu.ActionItem>
                <Menu.ActionItem
                  withLeftAccessory={<UIIcon src={DeleteLine} />}
                  onClick={onDeleteSection}
                >
                  {i18n.t("remove_section")}
                </Menu.ActionItem>
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    </>
  );
};
